"use client";
import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { 
  Calendar, 
  Clock, 
  User, 
  ArrowLeft, 
  Sparkles,
  BookOpen
} from 'lucide-react';

// استيراد Swiper و SwiperSlide من react-swiper
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';

// استيراد ستايلات Swiper
import 'swiper/css';
import 'swiper/css/pagination';

import { Button } from "@/components/ui/button";
import { BLOGS_DATA } from './blogsData';

// مكون كارت المقال العادي
const BlogCard = ({ post }) => {
  return (
    <div
      className="bg-white/85 backdrop-blur-md border border-slate-100 rounded-[2rem] shadow-[0_15px_40px_-15px_rgba(0,0,0,0.03)] hover:shadow-[0_30px_60px_-15px_rgba(0,74,173,0.12)] flex flex-col justify-between h-full transition-all duration-500 group/blog overflow-hidden w-full relative z-10 cursor-grab active:cursor-grabbing select-none"
    >
      <div className="flex flex-col flex-1">
        {/* صورة المقال */}
        <div className="w-full h-48 sm:h-52 overflow-hidden relative border-b border-slate-50">
          <div className="absolute top-4 right-4 z-20 bg-white/90 backdrop-blur-md text-[#004aad] text-xs font-black px-3 py-1.5 rounded-xl shadow-sm border border-slate-100">
            {post.category}
          </div>
          <img 
            src={post.imageSrc} 
            alt={post.title} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover/blog:scale-103 pointer-events-none"
          />
        </div>

        {/* محتوى الكارت */}
        <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
          <div className="space-y-3">
            {/* الميتا داتا */}
            <div className="flex items-center gap-4 text-gray-400 text-[11px] sm:text-xs font-bold">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span>{post.readTime}</span>
              </div>
            </div>

            {/* العنوان */}
            <h3 className="text-base sm:text-lg font-black text-gray-900 tracking-tight leading-snug group-hover/blog:text-[#004aad] transition-colors duration-300 line-clamp-2 min-h-[3.25rem]">
              {post.title}
            </h3>

            {/* الوصف المختصر */}
            <p className="text-gray-600 text-xs sm:text-[13px] leading-relaxed font-medium line-clamp-3">
              {post.desc}
            </p>
          </div>

          {/* الكاتب وزر القراءة */}
          <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-2 mt-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs font-bold">
              <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[#004aad]">
                <User className="w-3 h-3" />
              </div>
              <span className="truncate max-w-[100px]">{post.author}</span>
            </div>

            <Link href={`/blog/${post.id}`} passHref legacyBehavior>
              <Button 
                variant="secondary" 
                size="sm"
                className="text-xs font-black text-[#004aad] bg-blue-50/50 hover:bg-blue-50 rounded-xl group/btn gap-1.5 px-3 h-9 cursor-pointer"
              >
                <span>اقرأ المقال</span>
                <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-300 group-hover/btn:-translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Blogs() {
  // وضع آخر 3 مقالات في البداية متبوعين ببقية المقالات
  const lastThree = BLOGS_DATA.slice(-3);
  const firstFour = BLOGS_DATA.slice(0, -3);
  const sortedPosts = [...lastThree, ...firstFour];

  return (
    <section 
      className="w-full bg-slate-50/60 py-12 sm:py-16 px-4 sm:px-6 lg:px-8 font-sans relative overflow-hidden bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem]" 
      dir="rtl"
    >
      {/* خلفيات الإضاءة */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <motion.div 
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ repeat: Infinity, duration: 14, ease: "easeInOut" }}
          className="absolute -top-40 -left-40 w-96 h-96 bg-blue-100/40 rounded-full blur-[100px]"
        />
        <motion.div 
          animate={{ x: [0, -30, 0], y: [0, 30, 0] }}
          transition={{ repeat: Infinity, duration: 16, ease: "easeInOut" }}
          className="absolute bottom-20 -right-40 w-96 h-96 bg-purple-100/30 rounded-full blur-[100px]"
        />
      </div>

      <div className="max-w-7xl mx-auto space-y-10 md:space-y-12 relative z-10">
        
        {/* هيدر الصفحة */}
        <div className="text-center max-w-2xl mx-auto space-y-3 md:space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-[#004aad] text-xs font-black rounded-xl border border-blue-100/50 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#3fd5b7]" />
            <span>مدونة ليزرجيت الرسمية</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight">
            آخر الأخبار و<span className="text-[#004aad]">المقالات</span> الحصرية
          </h2>
          <p className="text-xs sm:text-sm md:text-[14px] text-gray-500 font-bold max-w-lg mx-auto leading-relaxed">
            اكتشف شروحات تقنية، أدلة استثمارية، ونصائح تشطيب هندسية متكاملة مقدمة من خبراء المنصة.
          </p>
          <div className="w-20 sm:w-28 h-1 bg-gradient-to-r from-transparent via-[#004aad] to-transparent mx-auto rounded-full mt-2" />
        </div>

        {/* السلايدر لعرض المقالات */}
        <div className="w-full relative px-2 sm:px-4">
          <Swiper
            modules={[Pagination, Autoplay]}
            spaceBetween={24}
            slidesPerView={3}
            loop={true}
            pagination={{ 
              clickable: true,
              dynamicBullets: true
            }}
            autoplay={{ 
              delay: 4500, 
              disableOnInteraction: false 
            }}
            breakpoints={{
              0: { slidesPerView: 1 },
              640: { slidesPerView: 1.5 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 }
            }}
            className="pb-14 blogs-swiper"
          >
            {sortedPosts.map((post) => (
              <SwiperSlide key={post.id} className="h-auto flex">
                <BlogCard post={post} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

      </div>
    </section>
  );
}